import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, Subject, catchError, debounceTime, first, switchMap, takeUntil, tap } from 'rxjs';
import { OttoEmailScannerKuarPreview, OttoEmailScannerKuarResult } from '../../models/otto-olsen-emailscanner-kuar';
import { OttoOlsenEmailScannerKuarService } from '../../services/otto-olsen-emailscanner-kuar.service';

/**
 * Opened from the "Opprett KUAR" link in the email scanner information mail.
 * Creates the KUAR entry (K.artnr -> OO.artnr) for the customer in Jeeves after the OO user confirms.
 */
@Component({
  selector: 'otto-olsen-emailscanner-create-kuar',
  templateUrl: './otto-olsen-emailscanner-create-kuar.component.html',
  styleUrls: ['./otto-olsen-emailscanner-create-kuar.component.scss'],
  standalone: false
})
export class OttoOlsenEmailScannerCreateKuarComponent implements OnInit, OnDestroy {
  customerId = '';
  kArtnr = '';
  ooArtnr = '';
  // The link had no OO.artnr, so the user has to enter it
  ooArtnrMissingInLink = false;

  preview?: OttoEmailScannerKuarPreview;
  loadingPreview = false;
  previewFailed = false;

  creating = false;
  result?: OttoEmailScannerKuarResult;
  technicalError = false;

  protected route = inject(ActivatedRoute);
  protected kuarService = inject(OttoOlsenEmailScannerKuarService);

  private ooArtnrChanged = new Subject<string>();
  private unsubscribe = new Subject<void>();

  ngOnInit(): void {
    this.ooArtnrChanged
      .pipe(
        tap(() => (this.loadingPreview = true)),
        debounceTime(400),
        switchMap(ooArtnr =>
          this.kuarService.getPreview(this.customerId, this.kArtnr, ooArtnr).pipe(
            // Keep listening for new OO.artnr after a failed preview
            catchError(() => {
              this.loadingPreview = false;
              this.previewFailed = true;
              return EMPTY;
            })
          )
        ),
        takeUntil(this.unsubscribe)
      )
      .subscribe(preview => this.setPreview(preview));

    this.route.queryParams.pipe(first()).subscribe(params => {
      this.customerId = (params.customerId || '').trim();
      this.kArtnr = (params.kArtnr || '').trim();
      this.ooArtnr = (params.ooArtnr || '').trim();
      this.ooArtnrMissingInLink = !this.ooArtnr;
      if (this.customerId) {
        this.ooArtnrChanged.next(this.ooArtnr);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onOoArtnrChange(ooArtnr: string): void {
    this.ooArtnr = ooArtnr;
    this.result = undefined;
    this.technicalError = false;
    this.ooArtnrChanged.next(ooArtnr);
  }

  get ooArtnrEntered(): boolean {
    return !!this.ooArtnr.trim();
  }

  get alreadyExists(): boolean {
    return !!this.preview?.productFound && !!this.kArtnr && this.preview.existingKArtnrForProduct === this.kArtnr;
  }

  get replacesExistingKArtnr(): boolean {
    return !!this.preview?.existingKArtnrForProduct && !this.alreadyExists;
  }

  get created(): boolean {
    return this.result?.status === 'CREATED' || this.result?.status === 'ALREADY_EXISTS';
  }

  get canCreate(): boolean {
    return (
      !!this.preview?.customerFound &&
      !!this.kArtnr &&
      !!this.preview.productFound &&
      !this.alreadyExists &&
      !this.loadingPreview &&
      !this.creating &&
      !this.created
    );
  }

  create(): void {
    if (!this.canCreate) {
      return;
    }
    this.creating = true;
    this.result = undefined;
    this.technicalError = false;
    this.kuarService
      .create({ customerId: this.customerId, kArtnr: this.kArtnr, ooArtnr: this.preview.productId })
      .pipe(first())
      .subscribe({
        next: result => {
          this.creating = false;
          this.result = result;
          this.technicalError = result.status === 'FAILED';
          if (result.preview) {
            this.preview = result.preview;
          }
        },
        error: () => {
          this.creating = false;
          this.technicalError = true;
        }
      });
  }

  private setPreview(preview: OttoEmailScannerKuarPreview): void {
    // Ignore previews for an OO.artnr that has been changed since the request was sent
    if ((preview.ooArtnr || '') !== this.ooArtnr.trim()) {
      return;
    }
    this.preview = preview;
    this.loadingPreview = false;
    this.previewFailed = false;
  }
}
